import { Link, NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { AfyanexLogo, AfyanexMark } from "@/components/AfyanexLogo";
import OnboardingWizard from "@/components/OnboardingWizard";
import AgencySwitcher from "@/components/AgencySwitcher";
import {
  LayoutDashboard,
  ClipboardList,
  Pill,
  Bell,
  AlertOctagon,
  Clock4,
  Users,
  Shield,
  FileSearch,
  Menu,
  X,
  LogOut,
  User,
  ChevronDown,
  CircleAlert,
  CalendarX,
  BadgeAlert,
  ArrowRight,
  Activity,
  CalendarDays,
  DollarSign,
  LineChart,
  Clock as ClockIcon,
  Settings as SettingsIcon,
  UserPlus,
  FileDown,
  Mic,
  ShieldCheck,
  ScrollText,
} from "lucide-react";
import AdminVoiceCommandModal from "@/components/AdminVoiceCommandModal";
import HelpDrawer from "@/components/HelpDrawer";

const NAV_SECTIONS = [
  {
    eyebrow: "Overview",
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-overview" },
    ],
  },
  {
    eyebrow: "Operations",
    items: [
      { to: "/admin/reports", label: "Reports", icon: ClipboardList, testid: "nav-reports" },
      { to: "/admin/schedule", label: "Schedule", icon: CalendarDays, testid: "nav-schedule" },
      { to: "/admin/mar", label: "MAR", icon: Pill, testid: "nav-mar" },
      { to: "/admin/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts" },
      { to: "/admin/incidents", label: "Incidents", icon: Activity, testid: "nav-incidents" },
    ],
  },
  {
    eyebrow: "People",
    items: [
      { to: "/admin/clients", label: "Clients", icon: Users, testid: "nav-clients" },
      {
        to: "/admin/team",
        label: "Team",
        icon: Shield,
        testid: "nav-team",
        end: true,
        children: [
          { to: "/admin/team/caregivers", label: "Caregivers", testid: "nav-team-caregivers" },
          { to: "/admin/team/admins", label: "Admins", testid: "nav-team-admins" },
        ],
      },
      { to: "/admin/applicants", label: "Applicants", icon: UserPlus, testid: "nav-applicants" },
    ],
  },
  {
    eyebrow: "Compliance",
    items: [
      { to: "/admin/compliance", label: "Compliance", icon: BadgeAlert, testid: "nav-compliance" },
      { to: "/admin/baa", label: "HIPAA BAA", icon: ShieldCheck, testid: "nav-baa" },
      { to: "/admin/audit-log", label: "Audit log", icon: ScrollText, testid: "nav-audit-log" },
    ],
  },
  {
    eyebrow: "Finance",
    items: [
      { to: "/admin/billing", label: "Billing", icon: DollarSign, testid: "nav-billing" },
      { to: "/admin/timecards", label: "Timecards", icon: ClockIcon, testid: "nav-timecards" },
      { to: "/admin/evv-export", label: "EVV Export", icon: FileDown, testid: "nav-evv-export" },
    ],
  },
  {
    eyebrow: "Insights",
    items: [
      { to: "/admin/analytics", label: "Analytics", icon: LineChart, testid: "nav-analytics" },
    ],
  },
  {
    eyebrow: "Agency",
    items: [
      { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
    ],
  },
];

const KIND_ICON = {
  late_arrival: Clock4,
  missed_visit: CalendarX,
  compliance: BadgeAlert,
};

function fmtAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch {
    return "";
  }
}

function NotificationsButton() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  const refresh = () => {
    api
      .get("/admin/notifications")
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], total: 0, by_kind: {} }));
  };

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const total = data?.total || 0;
  const items = data?.items || [];

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) refresh();
        }}
        className="relative btn-ghost !p-2"
        data-testid="notifications-button"
        aria-label={`Notifications (${total} unread)`}
      >
        <Bell className="h-5 w-5" />
        {total > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-semibold flex items-center justify-center"
            data-testid="notifications-count"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[360px] max-w-[92vw] max-h-[80vh] overflow-y-auto rounded-2xl border border-clinical-border bg-white shadow-xl z-50"
          data-testid="notifications-panel"
        >
          <div className="sticky top-0 bg-white flex items-center justify-between px-4 py-3 border-b border-clinical-border">
            <div>
              <div className="label-eyebrow">Inbox</div>
              <div className="font-heading text-sm font-semibold">
                {total === 0 ? "All clear" : `${total} alert${total === 1 ? "" : "s"} need attention`}
              </div>
            </div>
            {total > 0 && (
              <div className="flex items-center gap-1 text-[11px] text-clinical-subtext">
                {(data?.by_kind?.missed_visit || 0) > 0 && (
                  <span className="pill bg-destructive/10 text-destructive">
                    {data.by_kind.missed_visit} missed
                  </span>
                )}
                {(data?.by_kind?.late_arrival || 0) > 0 && (
                  <span className="pill bg-amber-100 text-amber-800">
                    {data.by_kind.late_arrival} late
                  </span>
                )}
              </div>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-clinical-subtext">
              <Activity className="h-6 w-6 mx-auto mb-2 text-clinical-subtext/60" />
              Nothing needs your attention right now.
            </div>
          ) : (
            <ul className="divide-y divide-clinical-border" data-testid="notifications-list">
              {items.map((n) => {
                const Icon = KIND_ICON[n.kind] || CircleAlert;
                const sevCls =
                  n.severity === "error"
                    ? "bg-destructive/10 text-destructive"
                    : n.severity === "warning"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-clinical-bg text-clinical-subtext";
                return (
                  <li key={n.id} data-testid={`notification-${n.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        if (n.link) navigate(n.link);
                      }}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-clinical-bg transition-colors"
                    >
                      <span className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg ${sevCls}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-clinical-text truncate">
                          {n.title}
                        </div>
                        <div className="text-[11px] text-clinical-subtext truncate">
                          {n.subtitle}
                        </div>
                      </div>
                      {n.timestamp && (
                        <span className="text-[10px] text-clinical-subtext shrink-0 mt-1">
                          {fmtAgo(n.timestamp)}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-clinical-bg transition-colors"
        data-testid="user-menu-button"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-clinical-primary text-white font-semibold text-xs">
          {(user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
        </span>
        <div className="hidden md:flex flex-col items-start leading-tight">
          <span className="text-sm font-medium text-clinical-text">{user?.name}</span>
          <span className="text-[10px] tracking-[0.1em] uppercase text-clinical-subtext">
            Agency Admin
          </span>
        </div>
        <ChevronDown className="h-3.5 w-3.5 text-clinical-subtext hidden md:inline" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-clinical-border bg-white shadow-lg z-50 overflow-hidden"
          data-testid="user-menu-panel"
        >
          <div className="px-4 py-3 border-b border-clinical-border">
            <div className="text-sm font-medium text-clinical-text truncate">{user?.name}</div>
            <div className="text-[11px] text-clinical-subtext truncate">{user?.email}</div>
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/my-profile");
            }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-clinical-text hover:bg-clinical-bg transition-colors"
            data-testid="user-menu-profile"
          >
            <User className="h-4 w-4 text-clinical-subtext" />
            My profile
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-destructive hover:bg-destructive/5 transition-colors"
            data-testid="user-menu-logout"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Sidebar({ onNavigate }) {
  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const handleSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  return (
    <nav className="h-full flex flex-col" data-testid="admin-sidebar">
      <div className="h-16 px-5 flex items-center border-b border-clinical-border shrink-0">
        <Link to="/admin" className="flex items-center gap-2.5" onClick={onNavigate}>
          <AfyanexLogo markSize={26} />
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto py-4">
        {NAV_SECTIONS.map((section) => (
          <div key={section.eyebrow} className="px-3 mb-5">
            <div className="px-2 mb-1.5 label-eyebrow">{section.eyebrow}</div>
            <ul className="space-y-0.5">
              {section.items.map((n) => {
                const isInParentSection = n.children
                  ? location.pathname === n.to ||
                    location.pathname.startsWith(`${n.to}/`)
                  : false;
                return (
                  <li key={n.to}>
                    <NavLink
                      to={n.to}
                      end={n.end}
                      onClick={onNavigate}
                      data-testid={n.testid}
                      className={({ isActive }) =>
                        `group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-clinical-primary/10 text-clinical-primary"
                            : "text-clinical-subtext hover:bg-clinical-bg hover:text-clinical-text"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <n.icon
                            className={`h-4 w-4 ${
                              isActive ? "text-clinical-primary" : "text-clinical-subtext group-hover:text-clinical-text"
                            }`}
                          />
                          {n.label}
                          {isActive && !n.children && (
                            <ArrowRight className="h-3.5 w-3.5 ml-auto text-clinical-primary" />
                          )}
                        </>
                      )}
                    </NavLink>
                    {n.children && isInParentSection && (
                      <ul className="mt-0.5 ml-6 border-l border-clinical-border pl-2 space-y-0.5">
                        {n.children.map((c) => (
                          <li key={c.to}>
                            <NavLink
                              to={c.to}
                              onClick={onNavigate}
                              data-testid={c.testid}
                              className={({ isActive }) =>
                                `block rounded-md px-2 py-1.5 text-[13px] transition-colors ${
                                  isActive
                                    ? "text-clinical-primary font-medium bg-clinical-primary/5"
                                    : "text-clinical-subtext hover:text-clinical-text hover:bg-clinical-bg"
                                }`
                              }
                            >
                              {c.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="px-3 pt-2 pb-3 border-t border-clinical-border">
        <button
          type="button"
          onClick={handleSignOut}
          className="group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-clinical-subtext hover:bg-rose-50 hover:text-rose-700 transition-colors"
          data-testid="sidebar-logout-button"
        >
          <LogOut className="h-4 w-4 group-hover:text-rose-700" />
          Sign out
        </button>
      </div>
      <div className="px-5 py-3 border-t border-clinical-border text-[10px] text-clinical-subtext">
        Confidential — PHI protected
      </div>
    </nav>
  );
}

function PageBreadcrumb() {
  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);
  if (segments.length <= 1) return null;
  // Capitalize words, swap dashes
  const label = (seg) =>
    seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <div className="text-xs text-clinical-subtext mb-3 hidden md:block">
      <Link to="/admin" className="hover:text-clinical-text">
        Admin
      </Link>
      {segments.slice(1).map((seg, i) => (
        <span key={seg}>
          <span className="mx-1.5 text-clinical-border">›</span>
          {i === segments.length - 2 ? (
            <span className="text-clinical-text font-medium">{label(seg)}</span>
          ) : (
            <span>{label(seg)}</span>
          )}
        </span>
      ))}
    </div>
  );
}

export default function AdminShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [agency, setAgency] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [voiceCmdOpen, setVoiceCmdOpen] = useState(false);
  const [baaStatus, setBaaStatus] = useState(null);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Cmd+K / Ctrl+K → open voice command palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setVoiceCmdOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Fetch agency once + decide if onboarding wizard should fire
  useEffect(() => {
    api.get("/agencies/me").then(({ data }) => {
      setAgency(data);
      const forced = searchParams.get("onboarding") === "1";
      if (forced || data?.onboarding_completed === false) {
        setShowWizard(true);
      }
    }).catch(() => {});
    // BAA status — show pending banner if not yet signed
    api.get("/baa/me").then(({ data }) => setBaaStatus(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeWizard = () => {
    setShowWizard(false);
    if (searchParams.has("onboarding")) {
      searchParams.delete("onboarding");
      setSearchParams(searchParams, { replace: true });
    }
    // Re-fetch agency to reflect onboarding_completed=true
    api.get("/agencies/me").then(({ data }) => setAgency(data)).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-clinical-bg" data-testid="admin-shell">
      {showWizard && <OnboardingWizard agency={agency} onClose={closeWizard} />}
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex fixed inset-y-0 left-0 w-60 bg-white border-r border-clinical-border z-40"
        aria-label="Primary navigation"
      >
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="mobile-sidebar-drawer">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white border-r border-clinical-border">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="md:ml-60 flex flex-col min-h-screen">
        {/* Top bar */}
        <header
          className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-clinical-border"
          data-testid="admin-top-bar"
        >
          <div className="flex h-16 items-center px-4 sm:px-6 gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="md:hidden btn-ghost !p-2"
              data-testid="open-sidebar-button"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="md:hidden flex items-center gap-2">
              <AfyanexMark size={24} />
            </div>
            <div className="hidden md:block">
              <div className="label-eyebrow">Agency console</div>
              <div className="text-sm font-medium text-clinical-text">
                <AgencySwitcher fallbackName={agency?.name || "Afyanex Care"} />
              </div>
            </div>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => setVoiceCmdOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-clinical-border bg-white px-2.5 py-1.5 text-xs font-medium text-clinical-subtext hover:border-clinical-accent hover:text-clinical-accent"
              title="Voice command (⌘K)"
              data-testid="topbar-voice-cmd"
            >
              <Mic size={14} />
              <span className="hidden sm:inline">Voice</span>
              <kbd className="hidden md:inline rounded bg-clinical-bg px-1 text-[10px] font-mono text-clinical-subtext">
                ⌘K
              </kbd>
            </button>
            <NotificationsButton />
            <UserMenu />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 mx-auto w-full max-w-6xl px-4 sm:px-6 py-6 sm:py-8">
          <PageBreadcrumb />
          {baaStatus && !baaStatus.is_signed && !location.pathname.startsWith("/admin/baa") && (
            <Link
              to="/admin/baa"
              className="mb-4 flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50/80 px-4 py-3 text-sm transition hover:bg-amber-100"
              data-testid="baa-pending-shell-banner"
            >
              <ShieldCheck className="h-4 w-4 text-amber-700 shrink-0" />
              <div className="flex-1">
                <span className="font-semibold text-amber-900">Sign your HIPAA BAA</span>
                <span className="text-amber-900/80 ml-1">
                  Required before processing real patient data. Takes 2 minutes.
                </span>
              </div>
              <span className="text-amber-700 text-xs font-semibold whitespace-nowrap">Sign now →</span>
            </Link>
          )}
          <div key={location.pathname} className="animate-fade-up">
            {children}
          </div>
        </main>
      </div>

      <AdminVoiceCommandModal
        open={voiceCmdOpen}
        onClose={() => setVoiceCmdOpen(false)}
      />
      <HelpDrawer />
    </div>
  );
}
